import React from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import MDBox from '../MDBox';
import MDTypography from '../MDTypography';

// Official NOAA GOES-West imagery URL for Pacific Southwest GeoColor
const GOES_WEST_IMAGE_URL = 'https://cdn.star.nesdis.noaa.gov/GOES18/ABI/SECTOR/psw/GEOCOLOR/GOES18-PSW-GEOCOLOR-600x600.gif';
const NOAA_GOES_PAGE_URL = 'https://www.star.nesdis.noaa.gov/GOES/sector.php?sat=G18&sector=psw';

function SatelliteContextSection() {
  return (
    <MDBox mt={6} mb={3}>
      {/* Section Title */}
      <MDTypography variant="h4" fontWeight="medium" color="info" textTransform="uppercase" textAlign="left" mb={1}>
        Satellite Context (GOES-18)
      </MDTypography>
      
      {/* Introduction */}
      <MDTypography variant="body1" color="text" textAlign="left" mb={3}>
        This section shows recent GOES-West satellite imagery over the California / Pacific Southwest region. 
        Fire detection systems analyze thermal signals from imagery like this, while our wildfire danger model 
        determines how sensitive the detection threshold should be.
      </MDTypography>

      <Grid container spacing={3}>
        {/* Satellite Image Panel */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
                Latest GOES-18 Imagery
              </MDTypography>
              
              <MDBox 
                sx={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  minHeight: '400px',
                }}
              >
                <img 
                  src={GOES_WEST_IMAGE_URL} 
                  alt="GOES-18 Pacific Southwest GeoColor Satellite Imagery"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<p style="padding: 20px; text-align: center; color: #666;">Unable to load satellite imagery. Please visit the official NOAA GOES page.</p>';
                  }}
                />
              </MDBox>

              <MDBox mt={2} textAlign="center">
                <Button
                  variant="outlined"
                  color="primary"
                  href={NOAA_GOES_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                      borderColor: '#1565c0'
                    }
                  }}
                >
                  <MDTypography variant="button" fontWeight="medium" color="white">
                    View Official NOAA GOES Imagery
                  </MDTypography>
                </Button>
              </MDBox>

              <MDBox 
                mt={2} 
                p={2} 
                sx={{ 
                  backgroundColor: '#fff9e6', 
                  borderRadius: '8px',
                  borderLeft: '4px solid #ffc107'
                }}
              >
                <MDTypography variant="caption" color="text" fontStyle="italic">
                  <strong>Note:</strong> Image updates approximately every 10 minutes. 
                  Source: NOAA NESDIS STAR GOES-18 Pacific Southwest GeoColor
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        {/* Explanation Panel */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" color="dark" mb={2}>
                How This Connects to Detection
              </MDTypography>

              <MDTypography variant="body2" color="text" mb={2}>
                Satellite imagery provides the live visual and thermal context for fire detection. 
                A detection model would convert imagery like this into a heat-related signal. 
                Our wildfire danger model then adjusts the threshold used to decide whether that 
                signal is strong enough to flag a possible fire.
              </MDTypography>

              <MDBox 
                p={2} 
                mb={2}
                sx={{ 
                  backgroundColor: '#e3f2fd', 
                  borderRadius: '8px',
                  borderLeft: '4px solid #2196f3'
                }}
              >
                <MDTypography variant="button" fontWeight="bold" color="text" mb={1}>
                  Detection Pipeline:
                </MDTypography>
                <MDBox component="ol" pl={2} mb={0}>
                  <li>
                    <MDTypography variant="body2" color="text" mb={1}>
                      <strong>Satellite captures imagery</strong>
                      <br />
                      GOES-18 continuously collects visible and infrared imagery of the Earth, 
                      including thermal information that can reveal heat sources.
                    </MDTypography>
                  </li>
                  <li>
                    <MDTypography variant="body2" color="text" mb={1}>
                      <strong>Detection model extracts a signal</strong>
                      <br />
                      A fire detection model analyzes the thermal bands in the imagery and converts 
                      each location into a numeric signal representing how "fire-like" or hot it appears.
                    </MDTypography>
                  </li>
                  <li>
                    <MDTypography variant="body2" color="text" mb={1}>
                      <strong>Threshold is set based on wildfire danger (FWI)</strong>
                      <br />
                      Using current weather conditions, the Fire Weather Index (FWI) estimates fire risk. 
                      Higher risk levels lower the detection threshold, making the system more sensitive.
                    </MDTypography>
                  </li>
                  <li>
                    <MDTypography variant="body2" color="text">
                      <strong>Signal is compared to threshold → detection decision</strong>
                      <br />
                      The extracted signal is compared against the threshold.
                      <br />
                      • If signal ≥ threshold → potential fire detected
                      <br />
                      • If signal &lt; threshold → no fire detected
                    </MDTypography>
                  </li>
                </MDBox>
              </MDBox>

              <MDBox 
                p={2} 
                sx={{ 
                  backgroundColor: '#fff3e0', 
                  borderRadius: '8px',
                  border: '1px solid #ffb74d'
                }}
              >
                <MDTypography variant="body2" color="text">
                  <strong>Important:</strong> This displayed image is contextual satellite imagery. 
                  The current web app is not directly running a full fire detector on the image itself; 
                  instead, it demonstrates how wildfire danger conditions would influence the detector's 
                  sensitivity.
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default SatelliteContextSection;
